import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Mail, ShieldCheck, Zap } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tier: 'premium_economy' | 'first_class';
  duration: string;
  price: number;
}

const TIER_LABEL: Record<'premium_economy' | 'first_class', string> = {
  premium_economy: 'Premium Economy',
  first_class: 'First Class',
};

export const GuestBoostModal = ({ open, onOpenChange, tier, duration, price }: Props) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (!open) setLoading(false);
  }, [open]);

  const startCheckout = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast({ title: 'Check the email', description: 'Enter a valid email address.', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-guest-boost-checkout', {
        body: { email: trimmed, tier, duration },
      });
      if (error || !data?.url) throw new Error(error?.message || 'Could not start checkout');
      window.location.href = data.url as string;
    } catch (err: any) {
      toast({
        title: 'Something went wrong',
        description: err?.message || 'Please try again.',
        variant: 'destructive',
      });
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>
            {TIER_LABEL[tier]} Boost · {duration.replace('-Day', '')} days
          </DialogTitle>
          <DialogDescription>One-off payment — no subscription, nothing to cancel.</DialogDescription>
        </DialogHeader>

        <form onSubmit={startCheckout} className="space-y-4">
          <div className="flex items-center justify-between rounded-lg bg-muted/50 px-4 py-3">
            <span className="text-sm">Total due today</span>
            <span className="text-lg font-bold text-primary">€{price.toFixed(2)}</span>
          </div>

          <div className="space-y-2">
            <Label htmlFor="guest-boost-email">Email for your receipt and login link</Label>
            <div className="relative">
              <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="guest-boost-email"
                type="email"
                required
                autoFocus
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="h-12 pl-9 text-base"
                disabled={loading}
              />
            </div>
          </div>

          <Button type="submit" size="lg" className="w-full min-h-[48px]" disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Opening secure checkout…
              </>
            ) : (
              <>
                <Zap className="mr-1.5 h-4 w-4" />
                Pay €{price.toFixed(2)} &amp; unlock now
              </>
            )}
          </Button>

          <p className="flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
            <ShieldCheck className="h-3 w-3" />
            Secure Stripe checkout · Unlocks instantly
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};
